// WS-14: far landscape 360° to the horizon (resort.json -> landscape). Engine-generic and off when the
// config is absent. Data: tools/build_landscape.py -> resorts/<id>/landscape/{landscape.json, mid.webp, far.webp}
// (MML 10 m DEM + Maastotietokanta land cover). Schema: skill references/landscape.md.
import * as THREE from 'three/webgpu';
import { float, vec3, vec4, uniform, attribute, property, positionWorld, cameraPosition, diffuseColor, instancedBufferAttribute, materialColor,
  mix, smoothstep, clamp, max, abs, length, normalize, exp, sqrt, select, If, Fn, renderGroup } from 'three/tsl';
import { onColor, onFog, seam, pname } from './gpu/chain.js';
import { sunGlowAt, skyGradient, skyBelow } from './sun.js'; // WS-66 / WS-75 sky: the sky's own formula, shared with the dome
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { releaseCpu } from './gpu/release.js'; // VIS-267 round 2: freezeFar

/** WS-17: the sky's colour right behind a far-ground pixel (the dome's gradient + the sun's glow, without the disc
 *  and the moon), so where the ground ends below the horizon it dissolves into the sky instead of drawing a line. */
function rimSky(S) {
  const d = normalize(positionWorld.sub(cameraPosition).add(vec3(0.0, 1e-3, 0.0))).toVar();
  const col = skyGradient(S, d);
  col.addAssign(sunGlowAt(d, S.sunDir, S.sunGlare, S.sunGlow)); // WS-66
  return skyBelow(S, d, col);
}

export const CLS = { FOREST: 0, OPEN: 1, SPARSE: 2, WATER: 3, BUILT: 4 };

async function readGrid(url, g) {
  const img = await new Promise((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = no; i.src = url; });
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(img, 0, 0);
  const px = ctx.getImageData(0, 0, img.width, img.height).data, n = g.n;
  const h = new Float32Array(n * n), cls = new Uint8Array(n * n);
  for (let i = 0; i < n * n; i++) { h[i] = g.minH + (px[i * 4] * 256 + px[i * 4 + 1]) / 10; cls[i] = px[i * 4 + 2]; }
  return { ...g, h, cls, maxX: g.minX + g.size, maxZ: g.minZ + g.size };
}

/** Load the landscape package. Returns null (with a warning) if it is missing. */
export async function loadLandscape(base, cfg) {
  try {
    const meta = await fetch(base + (cfg.src || 'landscape/landscape.json')).then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    const grids = await Promise.all(meta.grids.map((g) => readGrid(base + g.png, g)));
    return { meta, grids, cfg };
  } catch (e) {
    console.warn('landscape: package not found, run tools/build_landscape.py', e);
    degraded('landscape', e);
    return null;
  }
}

function bilinear(g, arr, x, z) {
  const fx = Math.min(g.n - 1.0001, Math.max(0, (x - g.minX) / g.spacing)), fz = Math.min(g.n - 1.0001, Math.max(0, (z - g.minZ) / g.spacing));
  const i = Math.floor(fx), j = Math.floor(fz), u = fx - i, v = fz - j, n = g.n;
  return (arr[j * n + i] * (1 - u) + arr[j * n + i + 1] * u) * (1 - v) + (arr[(j + 1) * n + i] * (1 - u) + arr[(j + 1) * n + i + 1] * u) * v;
}
const inside = (g, x, z, m = 0) => x >= g.minX + m && x <= g.maxX - m && z >= g.minZ + m && z <= g.maxZ - m;

/** Absolute terrain height (m) from the finest grid that covers (x, z); the mid grid fades into the far one. */
export function landHeight(L, x, z) {
  const [mid, far] = L.grids;
  if (!inside(mid, x, z)) return bilinear(far, far.h, x, z);
  const e = Math.min(x - mid.minX, mid.maxX - x, z - mid.minZ, mid.maxZ - z), t = Math.min(1, e / 200);
  const hm = bilinear(mid, mid.h, x, z);
  return t >= 1 ? hm : hm * t + bilinear(far, far.h, x, z) * (1 - t);
}
/** Land-cover class at (x, z) (nearest cell of the finest grid). */
export function landClass(L, x, z) {
  const g = L.grids.find((gg) => inside(gg, x, z)) || L.grids[L.grids.length - 1];
  const i = Math.round(Math.min(g.n - 1, Math.max(0, (x - g.minX) / g.spacing))), j = Math.round(Math.min(g.n - 1, Math.max(0, (z - g.minZ) / g.spacing)));
  return g.cls[j * g.n + i];
}
/** WS-17: land-cover shares (0..1 per class) over a square footprint of half-size `rad` m around (x, z). A far
 *  mesh vertex stands for an area tens of metres wide: taking the nearest 10/40 m cell there aliased into big
 *  flat blotches (canopy up, open down) seen from low angles. Area shares give a smooth canopy and colour. */
export function landCover(L, x, z, rad, out = new Float32Array(5)) {
  out.fill(0);
  const n = 4, w = 1 / (n * n);
  for (let a = 0; a < n; a++) for (let b = 0; b < n; b++) {
    const k = landClass(L, x + ((a + 0.5) / n * 2 - 1) * rad, z + ((b + 0.5) / n * 2 - 1) * rad);
    out[k < 5 ? k : 1] += w;
  }
  return out;
}

/**
 * Far terrain mesh: rings from the core's edge vertices outwards to the radius, spacing growing with
 * distance (constant screen density). Ring 0 re-uses the core ground's own edge vertices, normals and
 * painted colours, so the join is watertight and has no shading or colour line.
 * opts: { core (core ground Mesh, PlaneGeometry nx x nz), coreTex (what its paint reads back: groundpaint.js probeOf,
 *         { width, height, strips, small }, kept current by main.js on a repaint), extent, heightY(x,z)
 *         (scene y of the outer surface), Y0, segments, material, treeRadius, sink(x,z) }
 */
export function buildFarTerrain(L, o) {
  const { extent: ex, Y0 } = o, cfg = L.cfg;
  const cx = (ex.minX + ex.maxX) / 2, cz = (ex.minZ + ex.maxZ) / 2, R = L.meta.radius * 0.995;
  const cpos = o.core.geometry.attributes.position, cnor = o.core.geometry.attributes.normal;
  const nx = o.nx, nz = o.nz;
  // Ring 0: core edge vertices, clockwise from the north-west corner (row 0 = minZ).
  const edge = [];
  for (let i = 0; i < nx - 1; i++) edge.push(i);                        // north edge, west -> east
  for (let j = 0; j < nz - 1; j++) edge.push(j * nx + nx - 1);          // east edge, north -> south
  for (let i = nx - 1; i > 0; i--) edge.push((nz - 1) * nx + i);       // south edge, east -> west
  for (let j = nz - 1; j > 0; j--) edge.push(j * nx);                   // west edge, south -> north
  const W = ex.maxX - ex.minX, D = ex.maxZ - ex.minZ, per = 2 * (W + D);
  const tOf = (x, z) => { // perimeter parameter of a point on the core rectangle, same order as `edge`
    if (Math.abs(z - ex.minZ) < 1e-3 && x < ex.maxX - 1e-3) return (x - ex.minX) / per;
    if (Math.abs(x - ex.maxX) < 1e-3 && z < ex.maxZ - 1e-3) return (W + z - ex.minZ) / per;
    if (Math.abs(z - ex.maxZ) < 1e-3 && x > ex.minX + 1e-3) return (W + D + ex.maxX - x) / per;
    return (2 * W + D + ex.maxZ - z) / per;
  };
  const rectAt = (t) => { // point on the core rectangle at perimeter parameter t
    let s = ((t % 1) + 1) % 1 * per;
    if (s < W) return [ex.minX + s, ex.minZ]; s -= W;
    if (s < D) return [ex.maxX, ex.minZ + s]; s -= D;
    if (s < W) return [ex.maxX - s, ex.maxZ]; s -= W;
    return [ex.minX, ex.maxZ - s];
  };
  // Painted colour at the core edge (sRGB canvas -> linear), so ring 0 matches the core texture exactly.
  // Round-4b fix: the 5x5 window must stay inside the canvas (it read transparent black past the east and
  // south edges, which painted a dark band along them: the "property line square").
  // VIS-267 W6: the canvas is painted in a worker; the paint reads back what this needs (o.coreTex, groundpaint.js probeOf).
  const tex = o.coreTex;
  const stripFor = (x0, y0) => tex.strips.find((s) => x0 >= s.x && y0 >= s.y && x0 + 5 <= s.x + s.w && y0 + 5 <= s.y + s.h) || null; // WS-19: the four 5 px border strips { x, y, w, h, d }
  const edgeCol = (x, z) => {
    const px = Math.min(tex.width - 3, Math.max(2, Math.round(((x - ex.minX) / W) * tex.width))), pz = Math.min(tex.height - 3, Math.max(2, Math.round(((z - ex.minZ) / D) * tex.height)));
    let r = 0, g = 0, b = 0;
    // WS-19 (load time): every call reads a 5x5 block on the core border; read the four 5-px border strips once
    // instead of one GPU readback per edge vertex (same pixels, same sums). Anything else (a point off the border: no
    // caller asks for one) takes the 10 m cell it lies in.
    const s = stripFor(px - 2, pz - 2);
    if (s) for (let yy = 0; yy < 5; yy++) for (let xx = 0; xx < 5; xx++) { const k = ((pz - 2 + yy - s.y) * s.w + (px - 2 + xx - s.x)) * 4; r += s.d[k]; g += s.d[k + 1]; b += s.d[k + 2]; }
    else { const m = tex.small, k = (Math.min(m.h - 1, Math.floor((pz / tex.height) * m.h)) * m.w + Math.min(m.w - 1, Math.floor((px / tex.width) * m.w))) * 4; r = m.d[k] * 25; g = m.d[k + 1] * 25; b = m.d[k + 2] * 25; }
    return new THREE.Color().setRGB(r / 25 / 255, g / 25 / 255, b / 25 / 255, THREE.SRGBColorSpace);
  };
  // The core texture averaged per land-cover class (10 m cells): the far ground uses the same snow tone as
  // the core for the same cover, so there is no colour step at the edge (config colours only far away).
  const sw = Math.max(2, Math.round(W / 10)), sh = Math.max(2, Math.round(D / 10)); // (= groundpaint.js probeOf's small copy)
  const colorsOf = (C) => { // WS-41: a function, so recolor() can redo it from a repainted canvas (live season switch)
  const spx = tex.small.d, acc = {};
  for (let j = 0; j < sh; j++) for (let i = 0; i < sw; i++) {
    const k = landClass(L, ex.minX + (i + 0.5) * 10, ex.minZ + (j + 0.5) * 10), o4 = (j * sw + i) * 4;
    const c = new THREE.Color().setRGB(spx[o4] / 255, spx[o4 + 1] / 255, spx[o4 + 2] / 255, THREE.SRGBColorSpace);
    (acc[k] ||= { r: 0, g: 0, b: 0, n: 0 }); acc[k].r += c.r; acc[k].g += c.g; acc[k].b += c.b; acc[k].n++;
  }
  const mean = (k, fb) => (acc[k]?.n > 20 ? new THREE.Color(acc[k].r / acc[k].n, acc[k].g / acc[k].n, acc[k].b / acc[k].n) : new THREE.Color(fb));
  const col = {
    open: C.open ? new THREE.Color(C.open) : mean(1, '#f1f5fa'), shade: C.forestFloor ? new THREE.Color(C.forestFloor) : mean(0, '#e6ecf4'),
    forest: new THREE.Color(C.forest || '#6d7b7f'), sparse: new THREE.Color(C.sparse || '#aab6bb'), water: new THREE.Color(C.water || '#d5e0ea'),
  };
  col.sparseFloor = col.shade.clone().lerp(col.open, 0.5);
  return col;
  };
  const col = colorsOf(cfg.colors || {});
  const canopy = cfg.canopy ?? 9;
  const P = [], N = [], COL = [], UV = [], idx = [];
  const c0 = new THREE.Color(), c1 = new THREE.Color();
  const pushV = (x, y, z, c) => { P.push(x, y, z); COL.push(c.r, c.g, c.b); UV.push(x / 16, z / 16); return P.length / 3 - 1; };
  // WS-17: per outer vertex, what the canopy needs later (tune(treeRadius) runs once the real tree radius is known,
  // which the tree budget may shrink, e.g. on phones): ground y, cover shares, distance to the centre, edge blend.
  const OUT = []; // { v, y, cov: Float32Array(5), r, near, edge: Color|null, sink }
  const cov = new Float32Array(5);
  // ring 0
  const r0 = edge.map((k) => {
    const x = cpos.getX(k), y = cpos.getY(k), z = cpos.getZ(k);
    const v = pushV(x, y, z, edgeCol(x, z)); N[v] = [cnor.getX(k), cnor.getY(k), cnor.getZ(k)];
    return { v, t: tOf(x, z) };
  });
  // outer rings
  const seg = o.segments, q = 1 + (2 * Math.PI) / seg, half = (W + D) / 4;
  const rings = [];
  let s = 1 + 6 / half;
  while (true) {
    const ring = [];
    const w = THREE.MathUtils.smoothstep(s, 1.4, 4); // square near the core, round towards the horizon
    for (let a = 0; a < seg; a++) {
      const t = a / seg, [bx, bz] = rectAt(t), dx = bx - cx, dz = bz - cz, len = Math.hypot(dx, dz);
      const rad = THREE.MathUtils.lerp(len * s, half * s * 1.12, w), x = cx + (dx / len) * Math.min(rad, R), z = cz + (dz / len) * Math.min(rad, R);
      const out = Math.max(ex.minX - x, x - ex.maxX, ex.minZ - z, z - ex.maxZ, 0);
      const sk = o.sink ? o.sink(x, z) : 0; // > 0 around off-site zone patches: no canopy, sink under the patch
      const near = 1 - THREE.MathUtils.smoothstep(out, 0, 150); // fade from the painted edge colour over 150 m
      const r = Math.hypot(x - cx, z - cz);
      // WS-17: cover shares over the vertex's own footprint (ring spacing ~2 % of r, never finer than the grid)
      landCover(L, x, z, Math.max(6, r * (q - 1) * 0.6, inside(L.grids[0], x, z) ? 0 : L.grids[1].spacing * 0.5), cov);
      const y = o.heightY(x, z) - sk;
      const v = pushV(x, y, z, c0.set(1, 1, 1));
      OUT.push({ v, y, cov: Float32Array.from(cov), r, near, sink: sk > 0, edge: near > 0 ? edgeCol(Math.min(ex.maxX, Math.max(ex.minX, x)), Math.min(ex.maxZ, Math.max(ex.minZ, z))) : null });
      ring.push(v);
    }
    rings.push(ring);
    if (s * half * 1.12 >= R) break;
    s *= q;
  }
  // stitch ring 0 (core edge, ~3 m spacing) to ring 1 (seg vertices) by perimeter parameter
  const r1 = rings[0].map((v, a) => ({ v, t: a / seg }));
  let i = 0, j = 0;
  const A = r0.length, B = r1.length;
  while (i < A || j < B) {
    const ai = r0[i % A], aj = r0[(i + 1) % A], bi = r1[j % B], bj = r1[(j + 1) % B];
    const ta = i + 1 <= A ? (i + 1 < A ? aj.t : 1) : 2, tb = j + 1 <= B ? (j + 1 < B ? bj.t : 1) : 2;
    if (ta <= tb) { idx.push(ai.v, aj.v, bi.v); i++; } else { idx.push(ai.v, bj.v, bi.v); j++; } // counter-clockwise from above
  }
  for (let r = 0; r < rings.length - 1; r++) {
    const a = rings[r], b = rings[r + 1];
    for (let k = 0; k < seg; k++) { const k1 = (k + 1) % seg; idx.push(a[k], a[k1], b[k], a[k1], b[k1], b[k]); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(COL, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(UV, 2));
  // WS-31: holes [[x0, z0, x1, z1], ...] (off-site zone terrain patches): triangles wholly inside a box are left out
  // (geometry, not a shader discard: discard costs early-z on every far fragment), so the zone's own ground mesh is
  // the only surface there. holeDepth[i] = how far the kept edge triangles reach into box i: the patch lies exactly
  // on them that far (surfaceSampler, which still sees every triangle).
  const holes = o.holes || [], holeDepth = holes.map(() => 0), fullIdx = idx;
  let keptIdx = idx;
  if (holes.length) {
    const inBox = (v, b) => { const x = P[v * 3], z = P[v * 3 + 2]; return x > b[0] && x < b[2] && z > b[1] && z < b[3] ? Math.min(x - b[0], b[2] - x, z - b[1], b[3] - z) : -1; };
    keptIdx = [];
    for (let t = 0; t < idx.length; t += 3) {
      let drop = false;
      holes.forEach((b, hi) => {
        const d = [inBox(idx[t], b), inBox(idx[t + 1], b), inBox(idx[t + 2], b)];
        if (d.every((v) => v >= 0)) drop = true; else holeDepth[hi] = Math.max(holeDepth[hi], ...d);
      });
      if (!drop) keptIdx.push(idx[t], idx[t + 1], idx[t + 2]);
    }
  }
  const idxAll = holes.length ? new THREE.Uint32BufferAttribute(fullIdx, 1) : null, idxKept = new THREE.Uint32BufferAttribute(keptIdx, 1);
  g.setIndex(idxKept);
  COL.length = 0; UV.length = 0; // VIS-267 round 2: copied into the attributes; pushV's scope (kept by recolor) held them
  g.setAttribute('aTree', new THREE.Float32BufferAttribute(new Float32Array(P.length / 3), 1));
  const mesh = new THREE.Mesh(g, o.material);
  // WS-17: the far forest continues the real trees instead of ending at a "tree disc" (round-4c feedback: the
  // forest read as a disc with an edge from far away, and the far canopy as big flat blotches from low angles).
  //  - The real trees thin out over `trees.fade` m before their radius (main.js) while crown cards (buildFarForest)
  //    fade in; beyond the cards the far mesh carries the forest as its mean tone at the real forest's density
  //    (crowns are a pixel or two wide there; drawing them as dots read as a halftone pattern).
  //  - The crown cover is view-dependent like a real forest: from straight above you see mostly snow between the
  //    crowns, at a low angle the trunks and crowns close up into a dark wall (1 - exp(-density * shadow area)).
  //  - Cover per vertex comes from land-cover area shares (landCover), never from the nearest cell.
  //  - The raised canopy surface (`canopy` m) starts only where the real trees have become sparse.
  const pos = g.attributes.position, colA = g.attributes.color, treeA = g.attributes.aTree, fade = cfg.trees?.fade ?? 280;
  const sparseK = cfg.trees?.sparse ?? 0.35, floor = new THREE.Color(), tmp = new THREE.Color();
  const C2 = cfg.colors || {};
  const forestU = { // WS-75: uniform nodes (.value as before)
    uTreeCell: uniform(12), uTreeCol: uniform(new THREE.Color(C2.canopy || '#22362d')),
    uCrown: uniform(new THREE.Vector3(21, 5, 7)), // top crown area m2, mean silhouette width m and height m (core tree models)
    uCanopyGain: uniform(cfg.canopyGain ?? 1.6), // forest cells are fully wooded; a vertex's share averages in clearings
  };
  mesh.userData.setTreeRadius = (Rt, dens) => {
    if (dens > 0) forestU.uTreeCell.value = 1 / Math.sqrt(dens);
    for (const p of OUT) {
      const [f0, f1, f2, f3, f4] = p.cov, fo = f1 + f4;
      const fc = THREE.MathUtils.smoothstep(p.r, Rt - fade, Rt);
      const farC = p.sink ? 0 : fc + (1 - fc) * (1 - farKeep(L.farThin, p.r)); // canopy shading where the trees thin out, and for the cards a budget left out (VIS-233)
      const farH = p.sink ? 0 : THREE.MathUtils.smoothstep(p.r, Rt - fade * 0.3, Rt + 250); // raised surface where they are sparse
      floor.setRGB(0, 0, 0).add(tmp.copy(col.shade).multiplyScalar(f0)).add(tmp.copy(col.open).multiplyScalar(fo))
        .add(tmp.copy(col.sparseFloor).multiplyScalar(f2)).add(tmp.copy(col.water).multiplyScalar(f3));
      c1.copy(floor);
      if (p.edge) c1.lerp(p.edge, p.near);
      colA.setXYZ(p.v, c1.r, c1.g, c1.b);
      treeA.setX(p.v, (f0 + sparseK * f2) * farC);
      pos.setY(p.v, p.y + canopy * (f0 + 0.5 * f2) * farH);
    }
    pos.needsUpdate = true; colA.needsUpdate = true; treeA.needsUpdate = true;
    if (idxAll) { g.setIndex(idxAll); g.computeVertexNormals(); g.setIndex(idxKept); } else g.computeVertexNormals(); // WS-31: smooth normals over the cut-out triangles too (hole rim, surfaceSampler)
    const nrm = g.attributes.normal; // ring 0 keeps the core's normals: no shading line at the join
    for (const { v } of r0) nrm.setXYZ(v, ...N[v]);
    nrm.needsUpdate = true;
    g.computeBoundingSphere();
    mesh.userData.treeRadius = Rt;
  };
  mesh.userData.setTreeRadius(o.treeRadius, 0);
  // WS-17: the rim of the disc dissolves into the haze (fog colour = sky horizon colour), so no camera pose shows
  // the landscape's edge against the sky; hills further in stay visible.
  const rim = { uLandC: uniform(new THREE.Vector2(cx, cz)), uLandRim: uniform(new THREE.Vector2(R * (cfg.rimFade ?? 0.72), R * 0.985)), ...forestU };
  // the sky's uniform nodes by reference (they follow the time of day): sun.js createSkyUniforms; `below`: the physical
  // sky's colour under the horizon (lazy/skyphys.js), so the rim fades into exactly what the dome shows behind it
  const sky = o.sky ? Object.fromEntries(['horizon', 'zenith', 'glow', 'belt', 'sunDir', 'sunGlare', 'sunGlow', 'below'].map((k) => [k, o.sky[k]])) : null;
  if (sky) rim.sky = sky;
  // WS-75: the canopy is a colour hook at stage 20 (after the bare-ground layers, before water), the
  // rim a fog hook. SEAMS.get(material).canopyK = the canopy's share of this pixel (the trees lane's far-forest haze
  // weighs its aerial perspective with it; 0 where there is no forest). material.userData.canopyDetail (lazy/atmos.js:
  // the far forest's fine pattern) turns the canopy share into its detailed one; called at the top level (its pattern
  // takes screen derivatives).
  const patchFar = farPatcher(rim, sky); // WS-41: a function, so another (season) material gets the same rim + canopy (VIS-267 round 2: made outside, farPatcher)
  patchFar(o.material);
  mesh.userData.patchMaterial = patchFar; // WS-41: the live season switch swaps the far material (snow / bare ground)
  mesh.userData.rim = rim;
  mesh.userData.holeDepth = holeDepth; mesh.userData.fullIndex = idxAll?.array ?? null; // WS-31: see the holes above (VIS-267 round 2: the Uint32 copy, not the build's number list; no holes: the geometry's index, the same numbers)
  mesh.userData.floor = { open: col.open.clone(), shade: col.shade.clone() }; // WS-31: snow tones per cover (zone patches match them)
  // WS-41: live season switch: the core canvas was repainted and the config colours changed. Ring 0 and the near-edge
  // blend take the canvas again, the cover tones are recomputed, then the vertex colours (setTreeRadius) are redone.
  mesh.userData.recolor = (colors = {}) => {
    const c2 = colorsOf(colors);
    for (const k of Object.keys(c2)) col[k].copy(c2[k]);
    for (const { v } of r0) { const e = edgeCol(P[v * 3], P[v * 3 + 2]); colA.setXYZ(v, e.r, e.g, e.b); }
    for (const q of OUT) if (q.edge) q.edge.copy(edgeCol(Math.min(ex.maxX, Math.max(ex.minX, P[q.v * 3])), Math.min(ex.maxZ, Math.max(ex.minZ, P[q.v * 3 + 2]))));
    if (colors.canopy || !mesh.userData.ws18) forestU.uTreeCol.value.set(colors.canopy || '#22362d'); // a linked tree layer (trees.js linkFar) sets it from its own bake
    mesh.userData.floor.open.copy(col.open); mesh.userData.floor.shade.copy(col.shade);
    mesh.userData.setTreeRadius(mesh.userData.treeRadius, 0);
  };
  mesh.receiveShadow = true;
  mesh.name = 'landscape-far';
  return mesh;
}

/** VIS-267 round 2 (phone memory): a map with one season (main.js !LIVE) never recolours or re-tunes its far ground
 *  after the build (setTreeRadius runs once, main.js; recolor and the zone patches' rim resample only on a live season
 *  switch): its rebuild functions go, and with them their per-vertex records (OUT, ring 0, the positions as numbers),
 *  and, once on the graphics chip, its colour, canopy (aTree), normal and uv arrays. Position and index stay: rays,
 *  and surfaceSampler (positions + fullIndex, no colour) when the ribbons are built again with zone roads. Call it after
 *  the first draw (and again until it returns with userData.cpuReleased: what is not uploaded yet stays). A map that can
 *  switch seasons live keeps everything. -> bytes freed */
export function freezeFar(mesh, renderer) {
  if (!mesh?.isMesh) return 0;
  delete mesh.userData.setTreeRadius; delete mesh.userData.recolor;
  return releaseCpu(renderer, mesh, { keep: ['position', 'index'] });
}

/** WS-17 / WS-41: the far ground's rim + canopy on a (season) material. VIS-267 round 2: a function of its own, outside
 *  buildFarTerrain: the material's hooks keep only rim and sky alive, not the build's per-vertex arrays (freezeFar). */
function farPatcher(rim, sky) {
  return (mat) => {
    const canopyK = property('float', pname('landCanopy'));
    seam(mat).canopyK = canopyK;
    onColor(mat, () => {
      const vTree = attribute('aTree', 'float');
      const vd = cameraPosition.sub(positionWorld).toVar();
      const se = clamp(abs(vd.y).div(max(length(vd), 1e-3)), 0.06, 1.0).toVar(); // sine of the view elevation
      const dens = float(1.0).div(rim.uTreeCell.mul(rim.uTreeCell));
      const cov = exp(rim.uCanopyGain.mul(dens).mul(rim.uCrown.x.add(rim.uCrown.y.mul(rim.uCrown.z).mul(sqrt(max(se.mul(se).oneMinus(), 0.0))).div(se))).negate()).oneMinus().toVar();
      // the crown cards (buildFarForest) carry the near part: here crowns are a pixel or two wide, so their mean tone;
      // canopyDetail runs its pattern only where vTree > 0.002 (VIS-267 W9): keep the test below the same
      const t = mat.userData.canopyDetail ? mat.userData.canopyDetail({ cov, tree: vTree, landP: positionWorld, landC: rim.uLandC }) : cov;
      canopyK.assign(select(vTree.greaterThan(0.002), t.mul(clamp(vTree.mul(1.25), 0.0, 1.0)), 0.0));
      diffuseColor.assign(vec4(mix(diffuseColor.rgb, rim.uTreeCol, canopyK), diffuseColor.a));
    }, 20);
    onFog(mat, (out, b) => { // after the fog: the rim fades into the sky right behind it (or the fog colour)
      const rimF = smoothstep(rim.uLandRim.x, rim.uLandRim.y, length(positionWorld.xz.sub(rim.uLandC)));
      if (sky) return Fn(() => { // WS-77: the sky behind the rim only where it shows (weight > 0; no derivatives inside the branch); a Fn: an onFog hook emits no statements of its own (gpu/chain.js)
        const res = vec4(out).toVar();
        If(rimF.greaterThan(0), () => { res.assign(vec4(mix(res.rgb, rimSky(sky), rimF), res.a)); });
        return res;
      })();
      if (!b.scene?.fog?.color) return out;
      return vec4(mix(out.rgb, uniform(b.scene.fog.color).setGroup(renderGroup), rimF), out.a); // VIS-267: the fog colour follows the hour: shared, uploaded once a render on every set
    }, 10);
  };
}

/**
 * WS-31: the far mesh's DRAWN surface (its own triangles, not the DEM it was sampled from) inside a box, so a mesh
 * or ribbon laid onto it meets it exactly. Returns (x, z, colour?, normal?) -> scene y (colour / normal = the
 * interpolated vertex attributes, so shading matches too) or null outside the box / mesh. box = [x0, z0, x1, z1].
 */
export function surfaceSampler(mesh, box, cell = 12) {
  const g = mesh.geometry, P = g.attributes.position, Cl = g.attributes.color, I = mesh.userData.fullIndex || g.index.array; // all triangles, incl. the ones cut out under a zone patch
  const [bx0, bz0, bx1, bz1] = box, nx = Math.max(1, Math.ceil((bx1 - bx0) / cell)), nz = Math.max(1, Math.ceil((bz1 - bz0) / cell));
  const grid = Array.from({ length: nx * nz }, () => []);
  for (let t = 0; t < I.length; t += 3) {
    const a = I[t], b = I[t + 1], c = I[t + 2];
    const x0 = Math.min(P.getX(a), P.getX(b), P.getX(c)), x1 = Math.max(P.getX(a), P.getX(b), P.getX(c));
    const z0 = Math.min(P.getZ(a), P.getZ(b), P.getZ(c)), z1 = Math.max(P.getZ(a), P.getZ(b), P.getZ(c));
    if (x1 < bx0 || x0 > bx1 || z1 < bz0 || z0 > bz1) continue;
    for (let i = Math.max(0, Math.floor((x0 - bx0) / cell)); i <= Math.min(nx - 1, Math.floor((x1 - bx0) / cell)); i++)
      for (let j = Math.max(0, Math.floor((z0 - bz0) / cell)); j <= Math.min(nz - 1, Math.floor((z1 - bz0) / cell)); j++) grid[j * nx + i].push(t);
  }
  return (x, z, out = null, outN = null) => {
    const i = Math.floor((x - bx0) / cell), j = Math.floor((z - bz0) / cell);
    if (i < 0 || j < 0 || i >= nx || j >= nz) return null;
    for (const t of grid[j * nx + i]) {
      const a = I[t], b = I[t + 1], c = I[t + 2];
      const ax = P.getX(a), az = P.getZ(a), v0x = P.getX(b) - ax, v0z = P.getZ(b) - az, v1x = P.getX(c) - ax, v1z = P.getZ(c) - az, px = x - ax, pz = z - az;
      const den = v0x * v1z - v1x * v0z;
      if (Math.abs(den) < 1e-9) continue;
      const u = (px * v1z - v1x * pz) / den, v = (v0x * pz - px * v0z) / den;
      if (u < -1e-6 || v < -1e-6 || u + v > 1 + 1e-6) continue;
      const w = 1 - u - v;
      if (out && Cl) out.setRGB(Cl.getX(a) * w + Cl.getX(b) * u + Cl.getX(c) * v, Cl.getY(a) * w + Cl.getY(b) * u + Cl.getY(c) * v, Cl.getZ(a) * w + Cl.getZ(b) * u + Cl.getZ(c) * v);
      const Nr = g.attributes.normal;
      if (outN && Nr) outN.set(Nr.getX(a) * w + Nr.getX(b) * u + Nr.getX(c) * v, Nr.getY(a) * w + Nr.getY(b) * u + Nr.getY(c) * v, Nr.getZ(a) * w + Nr.getZ(b) * u + Nr.getZ(c) * v).normalize();
      return P.getY(a) * w + P.getY(b) * u + P.getY(c) * v;
    }
    return null;
  };
}

/**
 * WS-17: far forest ring. Beyond the real-tree radius the forest continues as one InstancedMesh of 4-triangle
 * crown cards (a 4-sided cone, snow on the upper faces) out to `trees.farRadius`, so the forest has no edge at any
 * allowed camera distance; beyond that the far mesh's procedural canopy (same density, mean tone) takes over
 * where a crown is only a pixel or two wide. Density, species share and size follow the real trees.
 * o: { inner (real-tree radius), dens (trees / m2 on forest cells), centre [x, z], heightY(x, z), keep(x, z) (treeRules),
 *      outside(x, z) (true beyond the core), mobile }
 * Returns { mesh, outer } (mesh null when nothing to draw).
 */
export function buildFarForest(L, o) {
  const tc = L.cfg.trees || {}, fade = tc.fade ?? 280, sparseK = tc.sparse ?? 0.35;
  // VIS-233 phone set (owner 2026-10-21: "can be thinned out even more"): 3,000 crown cards over the same 2 km, the canopy tone fills the rest
  const max = o.mobile ? (tc.farMaxMobile ?? 3000) : (tc.farMax ?? 90000);
  const outer = o.mobile ? (tc.farRadiusMobile ?? 2000) : (tc.farRadius ?? 2800);
  const inner = o.inner, [cx, cz] = o.centre;
  L.farThin = null;
  if (!(o.dens > 0) || outer <= inner) return { mesh: null, outer: inner };
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 6000; i++) rnd(); // the draws of the old land-share sample: the trees stand where they did before VIS-233
  const r0 = Math.max(0, inner - fade), tries = Math.round(o.dens * Math.PI * (outer * outer - r0 * r0));
  const cand = []; // x, z, size, height, r: every card at the forest's own density
  for (let t = 0; t < tries; t++) {
    const a = rnd() * Math.PI * 2, r = Math.sqrt(r0 * r0 + rnd() * (outer * outer - r0 * r0)), x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
    const sz = 0.7 + rnd() * 0.65, tall = 1.25 + rnd() * 0.55, keep = rnd();
    if (o.outside && !o.outside(x, z)) continue;
    const k = landClass(L, x, z), base = k === CLS.FOREST ? 1 : k === CLS.SPARSE ? sparseK : 0;
    // fades in exactly where the real trees thin out (complementary), fades out where the canopy shading takes over
    const p = base * THREE.MathUtils.smoothstep(r, inner - fade, inner) * (1 - THREE.MathUtils.smoothstep(r, outer - fade * 1.4, outer));
    if (keep < p && (!o.keep || o.keep(x, z))) cand.push(x, z, sz, tall, r);
  }
  // VIS-233: the budget thins the ring, never shortens it (a ring cut short at 1.7 km showed its end). Full density where
  // the real trees hand over, then evenly thinner (farKeep: share k from `ramp` m further out to the ring's end); the far
  // mesh's canopy tone stands in for the cards left out (setTreeRadius), so the forest's mean tone stays. k is solved on
  // the cards themselves: card i stays while tau_i < k, so the `max`-th smallest tau keeps exactly `max`.
  const nc = cand.length / 5, ramp = Math.min(2 * fade, (outer - inner) / 2);
  if (nc > max) {
    const tau = new Float32Array(nc);
    for (let i = 0; i < nc; i++) { const s = THREE.MathUtils.smoothstep(cand[i * 5 + 4], inner, inner + ramp), u = rnd(); tau[i] = s > 0 ? 1 - (1 - u) / s : -Infinity; }
    const k = Math.max(0, tau.slice().sort()[max]);
    L.farThin = { inner, ramp, k };
    let w = 0;
    for (let i = 0; i < nc && w < max * 5; i++) if (tau[i] < k) { for (let j = 0; j < 5; j++) cand[w + j] = cand[i * 5 + j]; w += 5; }
    cand.length = w;
  }
  const pts = [];
  for (let i = 0; i < cand.length; i += 5) pts.push(cand[i], cand[i + 1], cand[i + 2], cand[i + 3]);
  if (!pts.length) return { mesh: null, outer };
  // crown card: 6-sided open cone from 3.5 m to 14.5 m (the core pine/spruce silhouette at this distance; 4 sides
  // read as square diamonds from above)
  const geo = new THREE.ConeGeometry(2.0, 11, 6, 1, true).translate(0, 3.5 + 5.5, 0).toNonIndexed();
  geo.computeVertexNormals();
  const gp = geo.attributes.position, gn = geo.attributes.normal, cols = [];
  // shade it like a full crown, not a steep spike: normals tilted up (a steep face seen from above is at grazing
  // incidence and mirrors the bright sky, so the cards read grey next to the real crowns)
  for (let i = 0; i < gn.count; i++) { const nx = gn.getX(i), ny = gn.getY(i) + 0.7, nz = gn.getZ(i), l = Math.hypot(nx, ny, nz); gn.setXYZ(i, nx / l, ny / l, nz / l); }
  const lo = new THREE.Color(0x15301f), hi = new THREE.Color(0x26462f), snow = new THREE.Color(0xe8eef5), c = new THREE.Color();
  const frost = L.cfg.trees?.frost ?? 1; // WS-35: crown frost share (0 outside winter, landscape.trees.frost in the season patch)
  for (let i = 0; i < gp.count; i++) { const k = (gp.getY(i) - 3.5) / 11; c.copy(lo).lerp(hi, k).lerp(snow, (0.02 + 0.1 * k) * frost); cols.push(c.r, c.g, c.b); } // frosted towards the top, like the core crowns seen from afar
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
  geo.deleteAttribute('uv');
  const n = pts.length / 4, mesh = new THREE.InstancedMesh(geo, new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.95, envMapIntensity: 0.4 }), n);
  if (!n) mesh.visible = false; // WS-75 life: no instances = a zero-size instance buffer (WebGPU: binding size is zero): never drawn
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), p = new THREE.Vector3(), s = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    const x = pts[i * 4], z = pts[i * 4 + 1], sz = pts[i * 4 + 2], tall = pts[i * 4 + 3];
    e.set(0, (x * 7 + z) % 6.28, 0); q.setFromEuler(e);
    m4.compose(p.set(x, o.heightY(x, z) - 0.3, z), q, s.set(sz * 1.25, sz * tall * 0.8, sz * 1.25));
    mesh.setMatrixAt(i, m4);
  }
  mesh.computeBoundingSphere();
  mesh.matrixAutoUpdate = false;
  mesh.castShadow = L.cfg.trees?.farShadows ?? true; // the real trees' shadows streak the snow; without these the ground brightens at the hand-over
  mesh.name = 'landscape-far-forest';
  return { mesh, outer, count: n };
}
/** VIS-233: the share of the far forest's cards kept at distance r from the centre (L.farThin from buildFarForest;
 *  null = all of them): 1 where the real trees hand over, easing to k over `ramp` m, k out to the ring's end. */
function farKeep(T, r) {
  return T ? 1 - (1 - T.k) * THREE.MathUtils.smoothstep(r, T.inner, T.inner + T.ramp) : 1;
}

/** Settlement lights at night (MTK buildings outside the core): one Points draw call.
 *  WS-32 (user, round 6: the far town lights were too big and bright and overpowered the resort's own lights):
 *  they are faint, small points by default. landscape.lights = true | false | { size, intensity, color }:
 *  size = screen px (no distance scaling), intensity = peak opacity at full night (additive; kept well under the
 *  bloom threshold 0.92 so they never bloom), color = hex. Old key landscape.lightSize still sets the size.
 *  Client 2026-10-16 (the town read as a big city): not every building is lit. landscape.json `buildings`
 *  ([x, z, use], one per building, tools/build_landscape.py) are lit by their use's share at night,
 *  landscape.lights.lit = { home, public, holiday, industrial, church, other } (0..1, engine LIT_DEFAULTS),
 *  picked by a fixed hash of the position, each with its own brightness (some windows dim). Old packages
 *  without `buildings`, or with the old `lights` list and no `lit` setting, keep one light per point.
 *  Defaults = option C of ?preview=town-lights, approved by the owner 2026-10-16 (VIS-101): few, small, crisp. */
export const LIGHT_DEFAULTS = { size: 1.4, intensity: 0.85, color: 0xffcf9a };
export const LIT_DEFAULTS = { home: 0.3, public: 0.45, holiday: 0.08, industrial: 0.15, church: 1, other: 0.02 };
const hash01 = (x, z, k) => { const s = Math.sin(x * 12.9898 + z * 78.233 + k * 37.719) * 43758.5453; return s - Math.floor(s); };
export function buildLights(L, heightY) {
  const LC = { ...LIGHT_DEFAULTS, ...(L.cfg.lightSize != null ? { size: L.cfg.lightSize } : {}), ...(typeof L.cfg.lights === 'object' && L.cfg.lights ? L.cfg.lights : {}) };
  const byUse = L.meta.buildings && (LC.lit || !L.meta.lights);
  const lit = { ...LIT_DEFAULTS, ...(LC.lit || {}) };
  const pts = byUse ? L.meta.buildings.filter(([x, z, use]) => hash01(x, z, 1) < (lit[use] ?? lit.other)) : L.meta.lights || [];
  const pos = new Float32Array(pts.length * 3), col = new Float32Array(pts.length * 3);
  pts.forEach(([x, z], i) => {
    pos[i * 3] = x; pos[i * 3 + 1] = heightY(x, z) + 4; pos[i * 3 + 2] = z;
    const k = byUse ? 0.6 + 0.4 * hash01(x, z, 2) : 1; // a lit window or two, up to a yard light
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = k;
  });
  const c = document.createElement('canvas'); c.width = c.height = 32;
  const x = c.getContext('2d'), grd = x.createRadialGradient(16, 16, 0, 16, 16, 16);
  grd.addColorStop(0, 'rgba(255,220,170,1)'); grd.addColorStop(0.35, 'rgba(255,180,110,0.55)'); grd.addColorStop(1, 'rgba(255,160,90,0)');
  x.fillStyle = grd; x.fillRect(0, 0, 32, 32);
  // WS-75: WebGPU draws point primitives 1 px wide, so the points are instanced sprites (PointsNodeMaterial on a
  // Sprite: one draw call, `size` screen px, no distance scaling, as before); colour = map x colour x the point's own
  const m = new THREE.PointsNodeMaterial({ size: LC.size, sizeAttenuation: false, map: new THREE.CanvasTexture(c), color: new THREE.Color(LC.color), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0, fog: false });
  m.positionNode = instancedBufferAttribute(new THREE.InstancedBufferAttribute(pos, 3));
  m.colorNode = materialColor.mul(vec4(instancedBufferAttribute(new THREE.InstancedBufferAttribute(col, 3)), 1.0));
  const p = new THREE.Sprite(m);
  p.count = pts.length;
  p.raycast = () => {}; // never picked
  p.name = 'landscape-lights';
  p.frustumCulled = false;
  p.visible = false;
  p.userData.intensity = LC.intensity; // main.js applyTime: opacity = night level x intensity
  return p;
}
